import React, { useEffect, useState } from 'react';
import { Loader2, ArrowLeft, Lock, TrendingUp } from 'lucide-react';
import { atlasApi, AtlasUser, ResultadoPesquisa, NpsResultado } from '../shared/atlasApi';

interface Props {
  user: AtlasUser;
  pesquisaId: string;
  onVoltar: () => void;
}

const corNps = (v: number | null) =>
  v == null ? 'text-gray-400' : v >= 50 ? 'text-emerald-600' : v >= 0 ? 'text-amber-600' : 'text-rose-600';

const isNps = (v: unknown): v is NpsResultado =>
  !!v && typeof v === 'object' && 'nps' in (v as object);

const Barra: React.FC<{ valor: number; max: number; cor?: string }> = ({ valor, max, cor = '#7c3aed' }) => (
  <div className="flex-1 h-2 bg-gray-100 rounded overflow-hidden">
    <div className="h-full rounded" style={{ width: `${max ? (valor / max) * 100 : 0}%`, backgroundColor: cor }} />
  </div>
);

const Resultado: React.FC<Props> = ({ user, pesquisaId, onVoltar }) => {
  const [data, setData] = useState<ResultadoPesquisa | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    atlasApi.getResultadoPesquisa(user, pesquisaId)
      .then(setData)
      .catch(e => setErro(e.message || 'Erro ao carregar resultado'))
      .finally(() => setLoading(false));
  }, [user, pesquisaId]);

  return (
    <div className="space-y-4">
      <button onClick={onVoltar} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>

      {loading ? (
        <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>
      ) : erro ? (
        <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>
      ) : !data ? null : !data.liberado ? (
        <div className="p-10 text-center bg-white rounded-2xl border border-gray-200">
          <Lock className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <h3 className="font-bold text-gray-700">Resultado ainda protegido</h3>
          <p className="text-sm text-gray-500 mt-1">
            São necessárias ao menos {data.minimo} respostas para liberar o resultado
            (proteção de anonimato). Atualmente: {data.total_respostas}.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <div>
              <h2 className="text-lg font-bold text-gray-900">{data.pesquisa.nome}</h2>
              <p className="text-sm text-gray-500">
                {data.total_respostas} respostas · {data.pesquisa.anonima ? 'anônima' : 'identificada'}
              </p>
            </div>
            {data.enps != null && (
              <div className="ml-auto bg-white rounded-2xl border border-gray-200 px-5 py-3 text-center">
                <div className="flex items-center gap-1 text-xs text-gray-400 font-medium justify-center"><TrendingUp className="w-4 h-4" /> eNPS</div>
                <p className={`text-3xl font-black ${corNps(data.enps)}`}>{data.enps}</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            {(data.perguntas || []).map(p => (
              <div key={String(p.pergunta_id)} className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="flex items-start gap-2">
                  <h3 className="font-semibold text-gray-900 flex-1">{p.texto}</h3>
                  <span className="text-xs text-gray-400">{p.total} resp.</span>
                </div>

                {/* NPS */}
                {p.tipo === 'nps' && p.nps && (
                  <div className="mt-3">
                    <p className={`text-2xl font-black ${corNps(p.nps.nps)}`}>NPS {p.nps.nps ?? '—'}</p>
                    <div className="grid grid-cols-3 gap-2 mt-2 text-center text-sm">
                      <div className="bg-emerald-50 rounded-lg py-2"><div className="font-bold text-emerald-600">{p.nps.promotores}</div><div className="text-xs text-emerald-500">Promotores</div></div>
                      <div className="bg-amber-50 rounded-lg py-2"><div className="font-bold text-amber-600">{p.nps.neutros}</div><div className="text-xs text-amber-500">Neutros</div></div>
                      <div className="bg-rose-50 rounded-lg py-2"><div className="font-bold text-rose-600">{p.nps.detratores}</div><div className="text-xs text-rose-500">Detratores</div></div>
                    </div>
                  </div>
                )}

                {/* Escala 1-5 */}
                {p.tipo === 'escala_5' && (
                  <div className="mt-3">
                    {p.media != null && <p className="text-2xl font-black text-purple-600">{p.media}<span className="text-sm font-medium text-gray-400"> / 5</span></p>}
                    <div className="space-y-1.5 mt-2">
                      {[5, 4, 3, 2, 1].map(n => {
                        const qtd = p.distribuicao?.[String(n)] || 0;
                        const max = Math.max(1, ...Object.values(p.distribuicao || {}));
                        return (
                          <div key={n} className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="w-3">{n}</span>
                            <Barra valor={qtd} max={max} />
                            <span className="w-6 text-right">{qtd}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Sim / Não */}
                {p.tipo === 'sim_nao' && (
                  <div className="grid grid-cols-2 gap-2 mt-3 text-center text-sm">
                    <div className="bg-emerald-50 rounded-lg py-2"><div className="font-bold text-emerald-600">{p.sim || 0}</div><div className="text-xs text-emerald-500">Sim</div></div>
                    <div className="bg-rose-50 rounded-lg py-2"><div className="font-bold text-rose-600">{p.nao || 0}</div><div className="text-xs text-rose-500">Não</div></div>
                  </div>
                )}

                {/* Texto livre */}
                {p.tipo === 'texto' && (
                  <div className="mt-3 space-y-2 max-h-72 overflow-y-auto">
                    {(p.textos || []).length === 0 ? (
                      <p className="text-sm text-gray-400">Sem comentários.</p>
                    ) : (
                      (p.textos || []).map((t, i) => (
                        <div key={i} className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">“{t}”</div>
                      ))
                    )}
                  </div>
                )}

                {/* Por departamento (NPS / escala) */}
                {p.por_departamento && Object.keys(p.por_departamento).length > 1 && (p.tipo === 'nps' || p.tipo === 'escala_5') && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1.5">Por área</p>
                    <div className="space-y-1">
                      {Object.entries(p.por_departamento).map(([dep, val]) => (
                        <div key={dep} className="flex items-center justify-between text-xs">
                          <span className="text-gray-500 truncate">{dep}</span>
                          <span className="font-semibold text-gray-700">
                            {isNps(val) ? (val.nps ?? '—') : (val ?? '—')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Resultado;
